import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { config, Font } from '../../constants'
import { getFromSessionCache } from '../../modules/cache'
import { Locale } from '../../modules/locale'
import MainModule from '../../modules/main'
import { Routing } from '../../modules/routing'
import { EMAIL_REGEX, unescapeHtml } from '../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'login', {
  toasts: {
    passwordRecoveryAlert:
      'Nous avons reçu votre demande de réinitialisation de mot de passe. Si un compte est associé à cette adresse courriel, vous recevrez un courriel qui contiendra les instructions de réinitialisation de mot passe.',
    passwordRecoverySuccess: 'Votre mot de passe a été réinitialisé avec succès.',
    createPasswordSuccess: 'Mot de passe créé avec succès!',
  },
  menu: {
    title: 'Pas encore de compte?',
    subtitle:
      "En vous inscrivant, vous recevrez des suggestions personnalisées d'activités selon vos goûts.",
    signup: "S'inscrire",
    google: 'Continuer avec Google',
    facebook: 'Continuer avec Facebook',
    toggle: {
      signup: 'Inscription',
      login: 'Connexion',
    },
  },
  form: {
    title: 'Connectez-vous',
    divider: 'OU',
  },
  email: {
    label: 'Adresse courriel',
    placeholder: 'Entrez votre email',
  },
  password: {
    label: 'Mot de passe',
    placeholder: 'Mot de passe',
  },
  submit: 'Me connecter',
  forgotPassword: 'Mot de passe oublié?',
  rememberMe: 'Se souvenir de moi',
  errors: {
    invalidEmail: 'Adresse courriel invalide',
    invalidEmailOrPassword: 'Courriel ou mot de passe invalide',
    general: 'Erreur de connexion',
    emailRequired: 'Ce champ est requis',
    passwordRequired: 'Ce champ est requis',
    noAccount: "Aucun compte n'est associé à cette adresse courriel",
  },
  providers: {
    google: 'Se connecter avec Google',
    facebook: 'Se connecter avec Facebook',
  },
  b2bApp: {
    title: 'Connectez-vous à Espace Pro',
    goToSignupLabel: "Vous n'avez pas encore de compte?",
    goToSignupButton: "S'inscrire",
    goToSignupHref: 'https://lavitrine.com/fr/s-inscrire',
  },
})

Locale.getInstance().registerKeySet('en', 'login', {
  toasts: {
    passwordRecoveryAlert:
      'We received your password reset request. If an account exists for the email address that you provided, you will receive an email containing instructions on how to reset your password.',
    passwordRecoverySuccess: 'Your password was successfully changed.',
    createPasswordSuccess: 'Password successfully created!',
  },
  menu: {
    title: "Don't have an account yet?",
    subtitle:
      'By signing up, you will receive personalized activity suggestions based on your preferences.',
    signup: 'Sign up',
    google: 'Continue with Google',
    facebook: 'Continue with Facebook',
    toggle: {
      signup: 'Sign up',
      login: 'Login',
    },
  },
  form: {
    title: 'Sign in',
    divider: 'OR',
  },
  email: {
    label: 'Email',
    placeholder: 'Enter your email',
  },
  password: {
    label: 'Password',
    placeholder: 'Password',
  },
  submit: 'Login',
  forgotPassword: 'Forgot your password?',
  rememberMe: 'Remember me',
  errors: {
    invalidEmail: 'Invalid email address',
    invalidEmailOrPassword: 'Invalid email or password',
    general: 'Login error',
    emailRequired: 'This field is required',
    passwordRequired: 'This field is required',
    noAccount: 'No account is associated with this email address',
  },
  providers: {
    google: 'Login with Google',
    facebook: 'Login with Facebook',
  },
  b2bApp: {
    title: 'Sign in to Pro Space',
    goToSignupLabel: "Don't have an account yet?",
    goToSignupButton: 'Sign up',
    goToSignupHref: 'https://lavitrine.com/en/signup',
  },
})

export interface LvcLogin {
  email: string
  password: string
  loading: boolean
  redirectTo?: string
  isInB2bApp?: boolean
}

export class LvcLogin extends MinzeElement {
  attrs: Attrs = ['redirect-to', 'is-in-b2b-app']
  reactive: Reactive = [
    ['email', ''],
    ['password', ''],
    ['loading', false],
  ]

  errors: any = {
    email: false,
    password: false,
    general: false,
  }

  onReady() {
    const mainModule = MainModule.getInstance()

    const urlParams = new URLSearchParams(window.location.search)
    const recoveryEmailSent = urlParams.get('recover-password-email-sent')
    const recoveryPasswordSuccess = urlParams.get('recover-password-success')
    const createPasswordSuccess = urlParams.get('create-password-success')
    if (recoveryEmailSent === 'true')
      mainModule.showToastAlert(Locale.getKey('login.toasts.passwordRecoveryAlert'), 7000)
    if (recoveryPasswordSuccess === 'true')
      mainModule.showToastAlert(Locale.getKey('login.toasts.passwordRecoverySuccess'))
    if (createPasswordSuccess === 'true')
      mainModule.showToastAlert(Locale.getKey('login.toasts.createPasswordSuccess'))

    this.rerender()
    this.email = localStorage.getItem('rememberedEmail') || ''
  }

  async redirectToSignup() {
    const mainModule = MainModule.getInstance()
    const lang = mainModule.getLang()
    const routing = mainModule.getRouting()

    try {
      await mainModule.analyticsTrack('page_login_cta_sign_up', {})
    } catch (error) {
      console.error('Error tracking analytics event:', error)
    }
    routing.goTo(Routing.ROUTES[lang].signup)
  }

  onLogin = async () => {
    for (const key in this.errors) {
      this.errors[key] = false
    }
    let error = null
    const emailElement: any = this.select('#email')
    const email = unescapeHtml(emailElement?.value)

    const passwordElement: any = this.select('#password')
    const password = unescapeHtml(passwordElement?.value)

    const rememberMeElement: any = this.select('[name="rememberMe"]')
    const rememberMe = rememberMeElement?.getAttribute('checked')
    const bRememberMe = rememberMe === 'true' || rememberMe === true

    const mainModule = MainModule.getInstance()
    if (!email) {
      this.errors.email = Locale.getKey('login.errors.emailRequired')
    } else if (!EMAIL_REGEX.test(email)) {
      this.errors.email = Locale.getKey('login.errors.invalidEmail')
    }

    if (!password) {
      this.errors.password = Locale.getKey('login.errors.passwordRequired')
    }

    let hasError = false

    for (const key in this.errors) {
      if (this.errors[key]) {
        hasError = true
      }
    }

    if (hasError) {
      this.rerender()
      return
    }

    if (email && password) {
      this.email = email
      this.password = password

      if (bRememberMe) {
        localStorage.setItem('rememberedEmail', email)
      } else {
        localStorage.removeItem('rememberedEmail')
      }
      this.loading = true
      error = await mainModule.getAuth().login(email, password, false, this.redirectTo)
      this.loading = false
      //check if the user has the partnerSignupProcessTriggered flag in the session cache
      const partnerSignupProcessTriggered = getFromSessionCache(
        'partnerSignupProcessTriggered',
        true
      )

      // if the user has the flag, it means he came from the "become a partner" button
      if (partnerSignupProcessTriggered) {
        // check if user is already a partner
        const isPartner = await mainModule.getAuth().isPartner()

        // if he is a partner, send to the partner dashboard
        if (isPartner) {
          window.location.href = config.proSpaceUrl[mainModule.getLang() as 'fr' | 'en']
        } else {
          // if he is not a partner, send him to the partner registration page
          mainModule.getRouting().goTo(Routing.ROUTES[mainModule.getLang()].partnerRegister)
        }
      }
    }

    if (error) {
      if (error.code === 'auth/user-not-found') {
        this.errors.email = Locale.getKey('login.errors.noAccount')
      } else if (error.code === 'auth/wrong-password') {
        this.errors.general = Locale.getKey('login.errors.invalidEmailOrPassword')
      } else {
        this.errors.password = true
        this.errors.email = true
        this.errors.general = Locale.getKey('login.errors.general')
      }
    }

    this.rerender()
  }

  async onloginWithGoogle() {
    this.loading = true
    const mainModule = MainModule.getInstance()
    await mainModule.getAuth().callGoogleSignIn(false, this.redirectTo)
  }

  async onloginWithFacebook() {
    this.loading = true
    const mainModule = MainModule.getInstance()
    await mainModule.getAuth().callFacebookSignIn(false, this.redirectTo)
  }

  onEnterKey(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.onLogin()
    }
  }

  html() {
    let rememberMe = !!localStorage.getItem('rememberedEmail') as boolean

    return /*html*/ `



<lvc-auth-layout ${this.isInB2bApp ? 'is-in-b2b-app' : ''}>
    <!-- Menu slot for the login screen -->
    ${
      this.isInB2bApp
        ? ``
        : /*html*/ `
      <div slot="auth-menu" class="auth-menu">
        <div class="auth-menu-header">
          <h1 class="title">${Locale.getKey('login.menu.title')}</h1>
          <p class="subtitle">${Locale.getKey('login.menu.subtitle')}</p>
          <button class="auth-menu-button" id="signup-button">${Locale.getKey('login.menu.signup')}</button>
          <div slot="icon">
            <lvc-charlie-waving-full-body-icon width="235px" height="auto" variant="cropped"></lvc-charlie-waving-full-body-icon>
          </div>
        </div>
      </div>
    `
    }

    <!-- Form slot for login fields -->
    <div slot="auth-form" class="auth-form">
      <div class="form-header">
      <div slot="auth-toggle">
      <div class="toggle-container">
        <button class="toggle" name="toggle-signup" id="signup-button">
          ${Locale.getKey('login.menu.toggle.signup')}
        </button>
        <button class="toggle-on" name="toggle-login" id="signin-button">
          ${Locale.getKey('login.menu.toggle.login')}
        </button>
      </div>
      </div>
        <h1 class="title">${Locale.getKey(this.isInB2bApp ? 'login.b2bApp.title' : 'login.form.title')}</h2>
        <div class="sso-buttons-container">
          <button class="sso-button" id="google-sign-provider-button"><lvc-google-icon></lvc-google-icon>${Locale.getKey('login.menu.google')}</button>
          <button class="sso-button" id="facebook-sign-provider-button"><lvc-facebook-icon variant="simple"></lvc-facebook-icon>${Locale.getKey('login.menu.facebook')}</button>
        </div>
        <div class="divider-wrapper">
          <div class="divider-line"></div>
          <div class="divider-text">${Locale.getKey('login.form.divider')}</div>
          <div class="divider-line"></div>
        </div>
      </div>
      ${
        this.errors.general
          ? `
          <lvc-box mt="12px" display="flex" width="100%" justifyContent="center">
            <lvc-error error="${this.errors.general}"></lvc-error>
          </lvc-box>
        `
          : ``
      }
      <div class="form-group">
        <div class="email-container">
          <lvc-input type="text" value="${this.email}" id="email" error="${this.errors.email}" default-value="${this.email}" name="email" label="${Locale.getKey('login.email.label')}" placeholder="${Locale.getKey('login.email.placeholder')}" height="56px" on-change="${this.onChangeCb}"></lvc-input>
        </div>
        <div class="passwords">
          <div class="password-container">
            <lvc-input type="password" id="password" value="${this.password}" error="${this.errors.password}" default-value="${this.password}" name="password" label="${Locale.getKey('login.password.label')}" placeholder="${Locale.getKey('login.password.placeholder')}" height="56px" width="100%" on-change="${this.handleInputCb}"></lvc-input>
          </div>
        </div>

        <div class="form-footer">
          <lvc-checkbox
            name="rememberMe"
            label="${Locale.getKey('login.rememberMe')}"
            default-value="${rememberMe}"
            secondary-style="true"
          ></lvc-checkbox>
          <a href="${MainModule.getInstance().getRouting().getRoute('forgotPassword')}" class="forgot-password">${Locale.getKey('login.forgotPassword')}</a>
        </div>

        <div class="login-button">
          <button id="btn-login" class="btn-login">${Locale.getKey('login.submit')}
            ${this.loading ? `<div class="btn-loader"></div>` : ``}
          </button>
        </div>
        ${
          this.isInB2bApp
            ? /*html*/ `
        <div>
          <lvc-box mt="24px" textAlign="center">
            <div class="b2b-app-no-account-yet">
              ${Locale.getKey('login.b2bApp.goToSignupLabel')}
              <a href="${Locale.getKey('login.b2bApp.goToSignupHref')}">${Locale.getKey('login.b2bApp.goToSignupButton')}</a>
            </div>
          </lvc-box>
        </div>
        `
            : ``
        }
      </div>
      
      <!-- Mobile footer icon slot -->
      <div slot="footer-icon-mobile" class="charlie-icon-mobile">
        <lvc-charlie-peacesign-icon width="235px" height="auto" variant="hand-only"></lvc-charlie-peacesign-icon>
      </div>
    </lvc-auth-layout>
    `
  }

  css = () => `
  :host {
    
    }
    a {
      color: #1E007D;
    }
    .b2b-app-no-account-yet {
      font-size: 14px;
      color: #1E007D;
      font-family: inherit;
      font-weight: ${Font.weight.medium};
      text-decoration: none;
    }
    .divider-wrapper {
      display: flex;
      align-items: center;
      margin-top: 1rem;
    }
    .divider-text {
      padding-left: 8px;
      padding-right: 8px;
      font-size: 14px;
    }
    .divider-line {
      height: 1px;
      background: white;
      flex-grow: 1;
    }
    .btn-loader {
      border: 2px solid #fff;
      border-top: 2px solid transparent;
      border-radius: 50%;
      width: 20px;
      height: 20px;
      animation: spin 1s linear infinite;
    }
    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
    .form-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .forgot-password {
      display: flex;
      justify-content: flex-end;
      width: 100%;
      font-size: 14px;
      color: #1E007D;
      font-family: inherit;
      font-weight: ${Font.weight.medium};
    }

    .charlie-icon-mobile {
      display: none;
    }

    lvc-charlie-peacesign-icon {
      display: flex;
    }

    .toggle-container {
      display: none;
    }

    lvc-charlie-waving-full-body-icon {
      display: flex;
      position: absolute;
      bottom: -8px;
      right: 90px;
    }

    .footer-icon {
      position: absolute;
      bottom: 0px;
      right: 40px;
    }

    lvc-google-icon, lvc-facebook-icon {
      display: flex;
      align-items: center;
      width: 24px;
      height: 24px;
    }
    lvc-facebook-icon {
      width: 12px;
      height: 12px;
    }
    
    .sso-buttons-container {
      display: flex;
      justify-content: center;
      flex-direction: row;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    button {
      cursor: pointer;
      font-family: inherit;
    }

    .btn-login{
      background-color: #1E007D;
      color: white;
      padding: 1rem 2rem;
      border: none;
      border-radius: 99px;
      font-size: 1rem;
      font-weight: ${Font.weight.medium};
      cursor: pointer;
      outline: none;
      width: fit-content;
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 14px;
    }
    .btn-login:hover {
      background: linear-gradient(131.14deg, #1E007D 19.64%, #000FE3 94.07%);
    }

    .login-button{
      display: flex;
      justify-content: center;
      z-index: 10;
    }

    .sso-button {
      font-size: 14px;
      background: transparent;
      color: #1E007D;
      border: 1px solid #1E007D;
      border-radius: 99px;
      height: 56px;
      font-weight: ${Font.weight.medium};
      font-family: inherit;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      gap: 8px;
      text-wrap: nowrap;
      flex-grow: 1;
      min-width: 230px;
      transition: transform 0.2s;
    }
    .sso-button:hover {
      transform: scale(1.03);
    }

    .auth-form{
      display: flex;
      flex-direction: column;
      gap: 2rem;
      pointer-events: ${this.loading ? 'none' : 'auto'};
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .passwords{
      display: flex;
      flex-direction: row;
      gap: 2rem;
      justify-content: space-between;
      align-items: flex-end;
    }

    .confirm-password-container, .password-container {
      width: 100%;
    }

    .password-container {
      position: relative;
    }

    .title {
      font-size: 2.5rem;
      font-weight: ${Font.weight.extraBold};
      margin: 0;
      line-height: 1.1;
      color: #1E007D;
    }

    .subtitle {
      font-size: 14px;
      margin: 0;
      font-weight: ${Font.weight.medium};
    }

    .auth-menu-header {
      display: flex;
      flex-direction: column;
      gap: 2rem;
    }

    .form-header {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .auth-menu-button{
      border: none;
      background: transparent;
      color: #1E007D;
      width: fit-content;
      padding: 1rem 2rem;
      font-weight: ${Font.weight.medium};
      font-family: inherit;
      position: relative;
    }
    .auth-menu-button::before {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: 999px;
      padding: 2px; /* control the border thickness */
      background: linear-gradient(45deg, #1E007D, #1E007D);
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
    }
    .auth-menu-button:hover {
      background: linear-gradient(131.14deg, #1E007D 19.64%, #000FE3 94.07%);
      background-clip: text;
      -webkit-background-clip: text;
      color: transparent;
    }
    .auth-menu-button:hover::before {
      background: linear-gradient(131.14deg, #1E007D 19.64%, #000FE3 94.07%);
    }

    @media only screen and (max-width: 991px) {
      .passwords {
        align-items: flex-end;
        flex-direction: column;
      }

      .sso-buttons-container {
        flex-direction: column;
        gap: 1rem;
      }

      lvc-charlie-waving-icon {
        display: none;
      }
      .charlie-icon-mobile {
        display: flex;
        justify-content: flex-end;
      }

      .toggle-container {
        display: flex;
        justify-content: center;
        border-radius: 99px;
        padding: 3px;
        border: 1px solid #1E007D;
      }

      .toggle {
        background: transparent;
        color: #1E007D;
        border: none;
        font-weight: ${Font.weight.medium};
        font-family: inherit;
        cursor: pointer;
        padding: 1rem;
        outline: none;
        width: 100%;
      }

      .toggle-on {
        background: #1E007D;
        color: white;
        border-radius: 99px;
        border: none;
        font-weight: ${Font.weight.medium};
        font-family: inherit;
        cursor: pointer;
        padding: 1rem;
        outline: none;
        width: 100%;
      }
    }
  `

  eventListeners: EventListeners = [
    ['#btn-login', 'click', this.onLogin.bind(this)],
    ['#google-sign-provider-button', 'click', this.onloginWithGoogle.bind(this)],
    ['#facebook-sign-provider-button', 'click', this.onloginWithFacebook.bind(this)],
    ['#email', 'keydown', (event: Event) => this.onEnterKey(event as KeyboardEvent)],
    ['#password', 'keydown', (event: Event) => this.onEnterKey(event as KeyboardEvent)],
    ['#signup-button', 'click', this.redirectToSignup.bind(this)],
  ]
}
